import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * 可搜索单选下拉：输入过滤并点选。
 * options: [{ value, label, disabled? }]
 * 下拉面板挂载到 document.body，避免被表格 overflow 裁切。
 *
 * 远程搜索：传入 onSearch 后，输入关键字会回调父组件拉数；
 * filterLocal=false 时列表完全以 options 为准（适合服务端模糊查）。
 *
 * antiAutofill：门禁等同表单有密码框时开启，禁止浏览器账号密码快速填充，
 * 仅允许用户键入模糊查询或点选下拉。
 */
export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = '请选择或搜索',
  disabled = false,
  allowClear = true,
  emptyText = '无匹配选项',
  className = '',
  panelClassName = '',
  /** 避免浏览器把可搜索下拉当成登录用户名（同表单有 password 时尤甚） */
  autoComplete = 'off',
  inputName,
  inputType = 'text',
  /** 输入变化回调（远程搜索用）；由父组件自行防抖 */
  onSearch = null,
  /** 为 false 时不做本地过滤，直接展示 options */
  filterLocal = true,
  /** 远程搜索加载中 */
  loading = false,
  /** 禁止浏览器账号密码管理器填充（门禁登录店铺等场景） */
  antiAutofill = false,
  /**
   * 下拉展开方向：
   * - auto：下方空间不足时向上展开（默认）
   * - top：固定向上
   * - bottom：固定向下
   */
  placement = 'auto',
}) {
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const blockAutofillRef = useRef(antiAutofill);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [panelStyle, setPanelStyle] = useState({});
  // 初始只读；antiAutofill 时仅在用户真正按键/粘贴后解锁，避免 focus 时被密码管理器填充
  const [blockAutofill, setBlockAutofill] = useState(antiAutofill);

  useEffect(() => {
    blockAutofillRef.current = blockAutofill;
  }, [blockAutofill]);

  useEffect(() => {
    if (!antiAutofill) {
      setBlockAutofill(false);
      blockAutofillRef.current = false;
      return;
    }
    setBlockAutofill(true);
    blockAutofillRef.current = true;
  }, [antiAutofill]);

  const selected = useMemo(
    () => options.find((opt) => String(opt.value) === String(value)) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!filterLocal) return options;
    const q = keyword.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => String(opt.label || '').toLowerCase().includes(q));
  }, [options, keyword, filterLocal]);

  const updatePanelPosition = () => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 4;
    const preferredMax = 260;
    const minHeight = 120;
    const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - 12);
    const spaceAbove = Math.max(0, rect.top - 12);
    const openUp = placement === 'top'
      || (placement !== 'bottom' && spaceBelow < minHeight && spaceAbove > spaceBelow);

    if (openUp) {
      const maxHeight = Math.min(preferredMax, Math.max(minHeight, spaceAbove));
      setPanelStyle({
        position: 'fixed',
        top: 'auto',
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: Math.max(rect.width, 160),
        maxHeight,
        overflowX: 'hidden',
        overflowY: 'auto',
        zIndex: 13000,
      });
      return;
    }

    const maxHeight = Math.min(preferredMax, Math.max(minHeight, spaceBelow));
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + gap,
      bottom: 'auto',
      left: rect.left,
      width: Math.max(rect.width, 160),
      maxHeight,
      overflowX: 'hidden',
      overflowY: 'auto',
      zIndex: 13000,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, filtered.length, loading, placement]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
        setKeyword('');
        if (antiAutofill) {
          setBlockAutofill(true);
          blockAutofillRef.current = true;
          if (inputRef.current) inputRef.current.readOnly = true;
        }
        if (typeof onSearch === 'function') onSearch('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onSearch, antiAutofill]);

  const displayValue = open ? keyword : (selected?.label || '');

  const unlockTyping = () => {
    if (inputRef.current) inputRef.current.readOnly = false;
    blockAutofillRef.current = false;
    setBlockAutofill(false);
  };

  const lockTyping = () => {
    if (!antiAutofill) return;
    if (inputRef.current) inputRef.current.readOnly = true;
    blockAutofillRef.current = true;
    setBlockAutofill(true);
  };

  const pick = (nextValue) => {
    onChange?.(nextValue);
    setOpen(false);
    setKeyword('');
    lockTyping();
    if (typeof onSearch === 'function') onSearch('');
  };

  const emptyLabel = loading
    ? '搜索中…'
    : emptyText;

  const panel = open && !disabled ? createPortal(
    <div
      ref={panelRef}
      className={`spo-search-select__panel spo-search-select__panel--portal${panelClassName ? ` ${panelClassName}` : ''}`}
      style={panelStyle}
    >
      {filtered.length === 0 ? (
        <div className="spo-search-select__empty">{emptyLabel}</div>
      ) : filtered.map((opt) => {
        const active = String(opt.value) === String(value);
        return (
          <button
            key={String(opt.value)}
            type="button"
            className={`spo-search-select__option${active ? ' is-on' : ''}${opt.disabled ? ' is-disabled' : ''}`}
            disabled={!!opt.disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (opt.disabled) return;
              pick(opt.value);
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>,
    document.body,
  ) : null;

  const openPanel = ({ unlockForTyping = false } = {}) => {
    if (disabled) return;
    if (antiAutofill) {
      // 用户主动点击打开：解锁以便聚焦后立刻模糊搜索（自动填充仍靠诱饵字段与 autocomplete 抑制）
      if (unlockForTyping) unlockTyping();
    } else if (blockAutofill) {
      setBlockAutofill(false);
    }
    setOpen(true);
    setKeyword('');
  };

  const effectiveAutoComplete = antiAutofill ? 'one-time-code' : autoComplete;
  const effectiveInputName = antiAutofill
    ? (inputName ? `${inputName}-query` : 'ipe-search-select-query')
    : inputName;

  const focusInput = () => {
    const el = inputRef.current;
    if (!el || disabled) return;
    el.focus();
    // 部分浏览器只读态不显示光标；主动点击后已解锁
    try {
      const len = el.value?.length ?? 0;
      el.setSelectionRange(len, len);
    } catch {
      /* search/readonly 下偶发不支持 */
    }
  };

  return (
    <div
      className={`spo-search-select${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={rootRef}
    >
      <div
        className="spo-search-select__control"
        ref={controlRef}
        onMouseDown={(e) => {
          // 在 focus 前打开，并保证点击控件任意处都聚焦到输入框
          if (disabled) return;
          if (e.button !== 0) return;
          if (e.target?.closest?.('.spo-search-select__clear')) return;
          openPanel({ unlockForTyping: true });
          if (e.target !== inputRef.current) {
            e.preventDefault();
            focusInput();
          }
        }}
      >
        <input
          ref={inputRef}
          type={antiAutofill ? 'search' : inputType}
          name={effectiveInputName}
          className="spo-input spo-search-select__input"
          value={displayValue}
          disabled={disabled}
          placeholder={selected ? selected.label : placeholder}
          autoComplete={effectiveAutoComplete}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="search"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          data-autofill="off"
          readOnly={blockAutofill}
          onFocus={() => {
            openPanel({ unlockForTyping: antiAutofill });
          }}
          onBlur={() => {
            if (antiAutofill) {
              lockTyping();
              return;
            }
            // 失焦后再锁上，下次 focus 仍可打断自动填充
            setBlockAutofill(true);
          }}
          onChange={(e) => {
            // 只读期间丢弃浏览器自动填充写入
            if (blockAutofillRef.current) return;
            const next = e.target.value;
            setKeyword(next);
            setOpen(true);
            if (typeof onSearch === 'function') onSearch(next);
          }}
          onPaste={(e) => {
            if (!antiAutofill || !blockAutofillRef.current) return;
            e.preventDefault();
            const text = e.clipboardData?.getData('text') || '';
            unlockTyping();
            setKeyword(text);
            setOpen(true);
            if (typeof onSearch === 'function') onSearch(text);
          }}
          onKeyDown={(e) => {
            if (antiAutofill && blockAutofillRef.current) {
              if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                unlockTyping();
                setKeyword(e.key);
                setOpen(true);
                if (typeof onSearch === 'function') onSearch(e.key);
                return;
              }
              if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                unlockTyping();
                setKeyword('');
                setOpen(true);
                if (typeof onSearch === 'function') onSearch('');
                return;
              }
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              const first = filtered.find((opt) => !opt.disabled);
              if (first) pick(first.value);
            }
            if (e.key === 'Escape') {
              setOpen(false);
              setKeyword('');
              lockTyping();
              if (typeof onSearch === 'function') onSearch('');
            }
            if (e.key === 'Backspace' && !keyword && allowClear && value) {
              onChange?.('');
            }
          }}
        />
        {allowClear && value && !disabled ? (
          <button
            type="button"
            className="spo-search-select__clear"
            aria-label={fixedUi("清除")}
            onClick={() => {
              onChange?.('');
              setKeyword('');
              lockTyping();
              if (typeof onSearch === 'function') onSearch('');
              focusInput();
            }}
          >
            ×
          </button>
        ) : (
          <span className="spo-search-select__caret" aria-hidden>▾</span>
        )}
      </div>
      {panel}
    </div>
  );
}
